import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { AuthModalProvider } from "@/components/AuthModal";
import { ConsultModalProvider } from "@/components/ConsultModal";
import { seedOnce } from "@/lib/api";
import { Toaster } from "sonner";

import Home from "@/pages/Home";
import About from "@/pages/About";
import CouchLanding from "@/pages/CouchLanding";
import MentalWellbeing from "@/pages/MentalWellbeing";
import CognitiveSupport from "@/pages/CognitiveSupport";
import Therapists from "@/pages/Therapists";
import TherapistDetail from "@/pages/TherapistDetail";
import CouchHowItWorks from "@/pages/CouchHowItWorks";
import CouchPricing from "@/pages/CouchPricing";
import ForwardLanding from "@/pages/ForwardLanding";
import ForwardHowItWorks from "@/pages/ForwardHowItWorks";
import Packages from "@/pages/Packages";
import Mentors from "@/pages/Mentors";
import MentorDetail from "@/pages/MentorDetail";
import BookSession from "@/pages/BookSession";
import Organisations from "@/pages/Organisations";
import Corporate from "@/pages/Corporate";
import Schools from "@/pages/Schools";
import CareerWorkshops from "@/pages/CareerWorkshops";
import GetInTouch from "@/pages/GetInTouch";
import Events from "@/pages/Events";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Career from "@/pages/Career";
import FAQ from "@/pages/FAQ";
import RefundCancellationPolicy from "@/pages/RefundCancellationPolicy";
import ReferUs from "@/pages/ReferUs";
import FreeConsultation from "@/pages/FreeConsultation";
import MemberDashboard from "@/pages/MemberDashboard";
import BookingDetail from "@/pages/BookingDetail";
import LegalPage from "@/pages/LegalPage";
import TestimonialSubmit from "@/pages/TestimonialSubmit";
import CookieBanner from "@/components/CookieBanner";
import AnalyticsInjector from "@/components/AnalyticsInjector";
import { ProfessionalSignupProvider } from "@/components/ProfessionalSignupModal";
import { CurrencyProvider } from "@/lib/currency";
import ProfessionalDashboard from "@/pages/ProfessionalDashboard";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import ScrollProgress from "@/components/ScrollProgress";
import AIChatWidget from "@/components/AIChatWidget";
import MobileBottomNav from "@/components/MobileBottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MagicLinkAuth from "@/pages/MagicLinkAuth";
import PageLoader from "@/components/PageLoader";

// Admin shell + per-section routes (true router-level code splitting — each
// section becomes its own chunk, loaded only when its URL matches).
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverviewRoute = lazy(() => import("@/pages/admin/SimpleRoutes").then((m) => ({ default: m.OverviewRoute })));
const AdminSettingsRoute = lazy(() => import("@/pages/admin/SimpleRoutes").then((m) => ({ default: m.SettingsRoute })));
const AdminTableRoute = lazy(() => import("@/pages/admin/AdminTableRoute"));
const AdminPendingProsRoute = lazy(() => import("@/pages/admin/AdminTableRoute").then((m) => ({ default: m.PendingProsRoute })));
const AdminNotifyRoute = lazy(() => import("@/pages/admin/SendNotificationPanel"));
const AdminCtaPerfRoute = lazy(() => import("@/pages/admin/CtaPerformancePanel"));
const AdminFaqInsightsRoute = lazy(() => import("@/pages/admin/FaqInsightsPanel"));
const AdminAccountRoute = lazy(() => import("@/components/ProfileSection"));

const adminFallback = <PageLoader testid="admin-lazy-loading" />;

function App() {
  useEffect(() => { seedOnce(); }, []);
  useEffect(() => {
    // Register the PWA service worker (production-friendly, no-ops if SW unsupported)
    if ("serviceWorker" in navigator) {
      // Page already controlled by an (old) SW: when the fresh SW takes over,
      // reload once so users never sit on a stale bundle / white page.
      if (navigator.serviceWorker.controller) {
        let refreshed = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (refreshed) return;
          refreshed = true;
          window.location.reload();
        });
      }
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => reg.update()).catch(() => { /* swallow */ });
      });
    }
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ProfessionalSignupProvider>
        <AuthModalProvider>
          <ConsultModalProvider>
          <CurrencyProvider>
            <Toaster position="top-right" richColors closeButton duration={4500} />
            <ScrollProgress />
            <ScrollToTop />
            <Suspense fallback={<div className="md-route-loading" data-testid="route-loading" aria-label="Loading page" />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/couch" element={<CouchLanding />} />
            <Route path="/couch/mental-wellbeing" element={<MentalWellbeing />} />
            <Route path="/couch/cognitive-support" element={<CognitiveSupport />} />
            <Route path="/couch/how-it-works" element={<CouchHowItWorks />} />
            <Route path="/couch/pricing" element={<CouchPricing />} />
            <Route path="/therapists" element={<Therapists />} />
            <Route path="/therapists/:id" element={<TherapistDetail />} />
            <Route path="/forward" element={<ForwardLanding />} />
            <Route path="/forward/how-it-works" element={<ForwardHowItWorks />} />
            <Route path="/forward/packages" element={<Packages />} />
            <Route path="/find-a-mentor" element={<Mentors />} />
            <Route path="/forward/book-session" element={<Navigate to="/find-a-mentor" replace />} />
            <Route path="/mentors" element={<Mentors />} />
            <Route path="/mentors/:id" element={<MentorDetail />} />
            <Route path="/book/:type/:id" element={<BookSession />} />
            <Route path="/organisations" element={<Organisations />} />
            <Route path="/organisations/corporate" element={<Corporate />} />
            <Route path="/organisations/schools" element={<Schools />} />
            <Route path="/organisations/career-workshops" element={<CareerWorkshops />} />
            <Route path="/contact" element={<GetInTouch />} />
            <Route path="/events" element={<Events />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<ArticleDetail />} />
            <Route path="/career" element={<Career />} />
            <Route path="/faqs" element={<FAQ />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />
            <Route path="/return-refund" element={<RefundCancellationPolicy />} />
            <Route path="/refund-policy" element={<RefundCancellationPolicy />} />
            <Route path="/refer" element={<ReferUs />} />
            <Route path="/free-consultation" element={<FreeConsultation />} />
            <Route path="/login" element={<Navigate to="/?auth=login" replace />} />
            <Route path="/register" element={<Navigate to="/?auth=register" replace />} />
            <Route path="/dashboard" element={<MemberDashboard />} />
            <Route path="/dashboard/bookings/:id" element={<BookingDetail />} />
            <Route path="/bookings/:id" element={<BookingDetail />} />
            <Route path="/professional/dashboard" element={<ProfessionalDashboard />} />
            <Route path="/auth/magic" element={<MagicLinkAuth />} />
            <Route path="/admin" element={<Suspense fallback={adminFallback}><AdminLayout /></Suspense>}>
              <Route index element={<AdminOverviewRoute />} />
              <Route path="overview" element={<Navigate to="/admin" replace />} />
              <Route path="account" element={<AdminAccountRoute />} />
              <Route path="notify" element={<AdminNotifyRoute />} />
              <Route path="settings" element={<AdminSettingsRoute />} />
              <Route path="cta_perf" element={<AdminCtaPerfRoute />} />
              <Route path="faq_insights" element={<AdminFaqInsightsRoute />} />
              <Route path="pending_pros" element={<AdminPendingProsRoute />} />
              <Route path=":section" element={<AdminTableRoute />} />
            </Route>
            <Route path="/privacy-policy" element={<LegalPage kind="privacy" />} />
            <Route path="/cookie-policy" element={<LegalPage kind="cookie" />} />
            <Route path="/terms-and-conditions" element={<LegalPage kind="terms" />} />
            <Route path="/share-your-story" element={<TestimonialSubmit />} />
          </Routes>
          </Suspense>
          <CookieBanner />
          <AnalyticsInjector />
          <AIChatWidget />
          <ScrollToTopButton />
          <MobileBottomNav />
          <PWAInstallPrompt />
          {/* SVG gooey filter for blob buttons */}
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
            <defs>
              <filter id="goo">
                <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
                <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 21 -7" result="goo" />
                <feBlend in2="goo" in="SourceGraphic" result="mix" />
              </filter>
            </defs>
          </svg>
          </CurrencyProvider>
          </ConsultModalProvider>
        </AuthModalProvider>
        </ProfessionalSignupProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
